import { Card, KpiGrid, KpiTile, type Column } from '@platform/design-system';
import type { TypedClient } from '@platform/openapi';
import type { ReportPeriod } from '../report-period.ts';
import { usePageContext } from '../usePageContext.ts';
import { RateBar } from './RateBar.tsx';
import { QueryView } from '../QueryView.tsx';
import { deliveryRateColumn, numericColumn } from './columns.ts';
import { StatisticsTable } from './StatisticsTable.tsx';
import { rateOf, useStatisticsFormat, useStatisticsReport, type ConfirmationReport } from './statistics.ts';
import styles from './Statistics.module.css';

type AgentRow = ConfirmationReport['agents'][number];

const ConfirmationFigures = ({ report }: { report: ConfirmationReport }) => {
  const { t } = usePageContext();
  const format = useStatisticsFormat('');
  const { decisions } = report;
  const tiles = [
    ['received', report.received],
    ['handled', decisions.handled],
    ['confirmed', decisions.confirmed],
    ['rejected', decisions.rejected],
    ['pending', report.pending],
    ['delivered', decisions.delivered],
    ['returned', decisions.returned],
  ] as const;
  const busiestReason = report.reasons[0]?.orders ?? 0;
  const columns: readonly Column<AgentRow>[] = [
    {
      id: 'agent',
      header: t('statistics.agent'),
      primary: true,
      cell: (row) => row.name ?? t('statistics.formerMember'),
    },
    numericColumn('handled', t('statistics.handled'), (row) => format.count(row.handled)),
    numericColumn('rate', t('statistics.confirmationRate'), (row) =>
      format.rate(rateOf(row.confirmed, row.handled)),
    ),
    deliveryRateColumn(t('statistics.deliveredAfter'), format),
    numericColumn('delay', t('statistics.confirmationDelay'), (row) => format.duration(row.averageSeconds)),
  ];
  return (
    <div className={styles['stack']}>
      <KpiGrid>
        {tiles.map(([key, value]) => (
          <KpiTile key={key} label={t(`statistics.confirmation_${key}`)} value={format.count(value)} />
        ))}
      </KpiGrid>
      <div className={styles['columns']}>
        <Card title={t('statistics.confirmationRatesTitle')}>
          <RateBar
            label={t('statistics.netConfirmation')}
            value={rateOf(decisions.confirmed, decisions.handled)}
            text={format.rate(rateOf(decisions.confirmed, decisions.handled))}
          />
          <RateBar
            label={t('statistics.globalConfirmation')}
            value={rateOf(decisions.confirmed, report.received)}
            text={format.rate(rateOf(decisions.confirmed, report.received))}
          />
        </Card>
        <Card title={t('statistics.responsivenessTitle')}>
          <p className={styles['headline']}>
            <strong>{format.duration(decisions.averageSeconds)}</strong>
            <span className={styles['muted']}>{t('statistics.confirmationDelayHelp')}</span>
          </p>
        </Card>
      </div>
      <StatisticsTable
        title={t('statistics.agentsTitle')}
        help={t('statistics.agentsHelp')}
        columns={columns}
        rows={report.agents}
        rowKey={(row) => row.agentId}
        emptyTitle={t('statistics.noDecisions')}
      />
      <Card title={t('statistics.reasonsTitle')}>
        {report.reasons.length === 0 ? (
          <p className={styles['muted']}>{t('statistics.noRejection')}</p>
        ) : (
          report.reasons.map((entry) => (
            <RateBar
              key={entry.reason}
              label={t(`orders.reason_${entry.reason}`)}
              value={rateOf(entry.orders, busiestReason)}
              text={format.count(entry.orders)}
              tone="warning"
            />
          ))
        )}
      </Card>
    </div>
  );
};

export const ConfirmationStatistics = ({ api, period }: { api: TypedClient; period: ReportPeriod }) => {
  const { t } = usePageContext();
  const query = useStatisticsReport('confirmation', period, {}, () =>
    api.get('/admin/statistics/confirmation', { query: period }),
  );
  return (
    <QueryView query={query} label={t('statistics.tab_confirmation')}>
      {(report) => <ConfirmationFigures report={report} />}
    </QueryView>
  );
};
